import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Building2, Plus, Edit, Power, LogOut, Users, GraduationCap,
  UserPlus, Palette, Upload, School, Globe, Phone, Mail, Search, LayoutDashboard, Trash2, Send, User, RefreshCw, Clock, LogIn
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import OwnerBackupPanel from './OwnerBackupPanel';
import ManagementPanel from './management/ManagementPanel';
import ReportTemplatesPanel from './management/ReportTemplatesPanel';
import { formatLastLogin, getLastLoginColor } from '../utils/lastLogin';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const SuperAdminDashboard = () => {
  const { logout, user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    code: '', name: '', primary_color: '#1e40af', secondary_color: '#0f172a',
    accent_color: '#f97316', address: '', phone: '', email: '',
    admin_username: '', admin_email: '', admin_password: '', admin_full_name: ''
  });
  const [editData, setEditData] = useState({
    name: '', primary_color: '', secondary_color: '', accent_color: '',
    address: '', phone: '', email: ''
  });
  const [activeTab, setActiveTab] = useState('schools');
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '', smtp_port: 587, smtp_email: '', smtp_password: '',
    imap_host: '', imap_port: 993, imap_ssl: true,
  });
  const [smtpLoading, setSmtpLoading] = useState(false);

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/schools');
      setSchools(res.data);
    } catch (err) {
      toast.error('Error al cargar las escuelas');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);

  const fetchSmtpConfig = useCallback(async () => {
    try {
      const res = await axios.get('/schools/superadmin/smtp');
      setSmtpConfig({
        smtp_host: res.data.smtp_host || '',
        smtp_port: res.data.smtp_port || 587,
        smtp_email: res.data.smtp_email || '',
        smtp_password: '',
        imap_host: res.data.imap_host || '',
        imap_port: res.data.imap_port || 993,
        imap_ssl: res.data.imap_ssl !== false,
      });
    } catch (err) { /* no config yet */ }
  }, []);

  useEffect(() => { if (activeTab === 'settings') fetchSmtpConfig(); }, [activeTab, fetchSmtpConfig]);

  const saveSmtpConfig = async () => {
    setSmtpLoading(true);
    try {
      const payload = {
        ...smtpConfig,
        smtp_port: parseInt(smtpConfig.smtp_port) || 587,
        imap_port: parseInt(smtpConfig.imap_port) || 993,
      };
      if (!payload.smtp_password) delete payload.smtp_password;
      await axios.put('/schools/superadmin/smtp', payload);
      toast.success('Configuración SMTP guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar SMTP');
    }
    setSmtpLoading(false);
  };

  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');

  const testSmtpConfig = async () => {
    const target = (smtpTestEmail || smtpConfig.smtp_email || '').trim();
    if (!target) {
      toast.error('Ingresa un email destino para la prueba');
      return;
    }
    setSmtpTesting(true);
    try {
      // Save first so the test uses the latest values
      const payload = { ...smtpConfig, smtp_port: parseInt(smtpConfig.smtp_port) || 587 };
      if (!payload.smtp_password) delete payload.smtp_password;
      await axios.put('/schools/superadmin/smtp', payload);
      const res = await axios.post('/schools/superadmin/smtp/test', { to_email: target });
      toast.success(res.data.message || 'Correo de prueba enviado');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error desconocido';
      toast.error(detail, { duration: 9000 });
    }
    setSmtpTesting(false);
  };

  const handleCreate = async () => {
    const missing = [];
    if (!formData.name) missing.push('Nombre de la escuela');
    if (!formData.admin_full_name) missing.push('Nombre completo del administrador');
    if (!formData.admin_email) missing.push('Email del administrador');
    if (!formData.admin_username) missing.push('Usuario del administrador');
    if (!formData.admin_password) missing.push('Contraseña del administrador');
    if (missing.length > 0) {
      toast.error(`Falta: ${missing.join(', ')}`);
      return;
    }
    try {
      // Backend auto-generates the code from the name; do not send a code.
      const payload = { ...formData };
      delete payload.code;
      await axios.post('/schools', payload);
      toast.success('Escuela creada exitosamente');
      setShowCreateDialog(false);
      setFormData({
        code: '', name: '', primary_color: '#1e40af', secondary_color: '#0f172a',
        accent_color: '#f97316', address: '', phone: '', email: '',
        admin_username: '', admin_email: '', admin_password: '', admin_full_name: ''
      });
      fetchSchools();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear la escuela');
    }
  };

  const handleEdit = async () => {
    if (!selectedSchool) return;
    try {
      const update = {};
      Object.entries(editData).forEach(([k, v]) => { if (v) update[k] = v; });
      await axios.put(`/schools/${selectedSchool.id}`, update);
      toast.success('Escuela actualizada');
      setShowEditDialog(false);
      fetchSchools();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleToggleActive = async (school) => {
    try {
      if (school.is_active) {
        await axios.delete(`/schools/${school.id}`);
        toast.success('Escuela desactivada');
      } else {
        await axios.put(`/schools/${school.id}`, { is_active: true });
        toast.success('Escuela activada');
      }
      fetchSchools();
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDeleteSchool = async (school) => {
    if (!window.confirm(`¿Estás seguro de ELIMINAR PERMANENTEMENTE la escuela "${school.name}" (${school.code})?\n\nEsto eliminará TODOS los datos: usuarios, estudiantes, profesores, calificaciones, facturas, etc.\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      await axios.delete(`/schools/${school.id}/permanent`);
      toast.success(`Escuela "${school.name}" eliminada permanentemente`);
      fetchSchools();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar escuela');
    }
  };

  const sendSchoolAdminCredentials = async (school) => {
    const confirmed = window.confirm(
      `¿Enviar credenciales al administrador de "${school.name}"?\n\nSolo se envía si el administrador no ha recibido sus credenciales aún.`
    );
    if (!confirmed) return;
    try {
      const res = await axios.post(`/schools/${school.id}/send-admin-credentials`);
      if (res.data.already_sent) {
        toast.info('El administrador ya recibió sus credenciales anteriormente.');
        return;
      }
      const status = res.data.sent ? 'Email enviado' : 'Sin SMTP configurado';
      toast.success(
        `Credenciales generadas:\nUsuario: ${res.data.username}\nContraseña: ${res.data.new_password}\n(${status})`,
        { duration: 20000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar credenciales');
    }
  };

  const resendSchoolAdminCredentials = async (school) => {
    const confirmed = window.confirm(
      `⚠️ ¿REGENERAR y reenviar credenciales al administrador de "${school.name}"?\n\nSe creará una CONTRASEÑA NUEVA. La contraseña anterior dejará de funcionar. Usa esto solo si el admin perdió acceso o pidió un reset.`
    );
    if (!confirmed) return;
    try {
      const res = await axios.post(`/schools/${school.id}/resend-admin-credentials`);
      const status = res.data.sent ? 'Email enviado' : 'Sin SMTP configurado';
      toast.success(
        `Contraseña regenerada:\nUsuario: ${res.data.username}\nContraseña nueva: ${res.data.new_password}\n(${status})`,
        { duration: 20000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al regenerar credenciales');
    }
  };

  const impersonateSchoolAdmin = async (school) => {
    const confirmed = window.confirm(
      `¿Entrar como administrador de "${school.name}" para dar soporte técnico?\n\nSe cerrará tu sesión de SuperAdmin y entrarás con la cuenta del admin de esa escuela. Para volver a ser SuperAdmin tendrás que salir y volver a entrar con tu usuario "jcarrero".`
    );
    if (!confirmed) return;
    try {
      await axios.post(`/auth/impersonate/${school.id}`);
      toast.success(`Entrando como admin de ${school.name}...`);
      // Full reload so the app picks up the new session from /auth/me
      setTimeout(() => { window.location.href = '/'; }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al impersonar');
    }
  };

  const handleLogoUpload = async (schoolId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`/schools/${schoolId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo subido exitosamente');
      fetchSchools();
    } catch (err) {
      toast.error('Error al subir el logo');
    }
  };

  const openEdit = (school) => {
    setSelectedSchool(school);
    setEditData({
      name: school.name, primary_color: school.primary_color,
      secondary_color: school.secondary_color, accent_color: school.accent_color,
      address: school.address || '', phone: school.phone || '', email: school.email || ''
    });
    setShowEditDialog(true);
  };

  const filteredSchools = schools.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalStudents = schools.reduce((a, s) => a + (s.student_count || 0), 0);
  const totalTeachers = schools.reduce((a, s) => a + (s.teacher_count || 0), 0);
  const activeSchools = schools.filter(s => s.is_active).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-jk.png" alt="JK Enterprise" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-slate-800">
                EduCore
                <span className="relative inline-block">
                  <span>®</span>
                  <span className="absolute left-full bottom-1/2 text-[7px] font-bold tracking-[0.1em] text-slate-500 leading-none ml-0.5">BRAND</span>
                </span>
              </h1>
              <p className="text-xs text-slate-500 mt-1">Panel Super Administrador</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:block">{user?.username}</span>
            <Button onClick={logout} variant="outline" size="sm"
              className="border-slate-300 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button variant={activeTab === 'schools' ? 'default' : 'outline'} onClick={() => setActiveTab('schools')}
            className={activeTab === 'schools' ? 'bg-blue-600 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
            data-testid="tab-schools">
            <Building2 className="w-4 h-4 mr-1" /> Escuelas
          </Button>
          <Button variant={activeTab === 'settings' ? 'default' : 'outline'} onClick={() => setActiveTab('settings')}
            className={activeTab === 'settings' ? 'bg-blue-600 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
            data-testid="tab-settings">
            <Mail className="w-4 h-4 mr-1" /> Configuración SMTP
          </Button>
          <Button variant={activeTab === 'profile' ? 'default' : 'outline'} onClick={() => setActiveTab('profile')}
            className={activeTab === 'profile' ? 'bg-blue-600 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
            data-testid="tab-owner-profile">
            <User className="w-4 h-4 mr-1" /> Mi perfil
          </Button>
          <Button variant={activeTab === 'management' ? 'default' : 'outline'} onClick={() => setActiveTab('management')} data-testid="tab-management">Gestión y actividad</Button>
          <Button variant={activeTab === 'report-templates' ? 'default' : 'outline'} onClick={() => setActiveTab('report-templates')} data-testid="tab-report-templates">Plantillas de notas</Button>
        </div>

        {activeTab === 'profile' && <OwnerBackupPanel user={user} />}
        {activeTab === 'management' && <ManagementPanel />}
        {activeTab === 'report-templates' && <ReportTemplatesPanel />}

        {activeTab === 'schools' && (<>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Escuelas Registradas', value: schools.length, icon: Building2, color: 'from-blue-500 to-blue-600' },
            { label: 'Escuelas Activas', value: activeSchools, icon: Power, color: 'from-emerald-500 to-emerald-600' },
            { label: 'Estudiantes Activos', value: totalStudents, icon: GraduationCap, color: 'from-purple-500 to-purple-600' },
            { label: 'Total Profesores', value: totalTeachers, icon: Users, color: 'from-orange-500 to-orange-600' },
          ].map((stat) => (
            <Card key={stat.label} className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                  </div>
                  <div className={`w-11 h-11 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar escuelas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
              data-testid="search-schools-input"
            />
          </div>
          <Button onClick={() => setShowCreateDialog(true)}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
            data-testid="create-school-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Escuela
          </Button>
        </div>

        {/* Schools Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : filteredSchools.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No hay escuelas registradas</p>
              <p className="text-slate-400 text-sm mt-1">Crea tu primera escuela para comenzar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredSchools.map((school) => (
              <Card key={school.id}
                className={`bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 ${!school.is_active ? 'opacity-60' : ''}`}
                data-testid={`school-card-${school.code}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {school.logo_url ? (
                        <img src={school.logo_url.startsWith('data:') ? school.logo_url : `${BACKEND_URL}${school.logo_url}`} alt={school.name}
                          className="w-16 h-16 rounded-xl object-contain border border-slate-200 bg-white p-1" />
                      ) : (
                        <div className="w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold text-2xl"
                          style={{ background: `linear-gradient(135deg, ${school.primary_color}, ${school.accent_color})` }}>
                          {school.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-slate-800 text-base">{school.name}</CardTitle>
                        <Badge variant="outline" className="border-slate-300 text-slate-500 text-xs mt-1">
                          {school.code}
                        </Badge>
                        <p className="text-[10px] text-blue-500 mt-1 font-mono">
                          {school.code.toLowerCase()}.tudominio.com
                        </p>
                      </div>
                    </div>
                    <Badge className={school.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                      {school.is_active ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: UserPlus, label: 'Admins', val: school.admin_count || 0 },
                      { icon: Users, label: 'Profesores', val: school.teacher_count || 0 },
                      { icon: GraduationCap, label: 'Estudiantes', val: school.student_count || 0 },
                    ].map((s) => (
                      <div key={s.label} className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-slate-700">{s.val}</p>
                        <p className="text-[10px] text-slate-500">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Colors Preview */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Colores:</span>
                    <div className="flex gap-1">
                      {[school.primary_color, school.secondary_color, school.accent_color].map((c, i) => (
                        <div key={i} className="w-5 h-5 rounded-full border border-slate-200" style={{ backgroundColor: c }} title={c} />
                      ))}
                    </div>
                  </div>

                  {/* Info */}
                  {(school.phone || school.email) && (
                    <div className="space-y-1">
                      {school.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{school.phone}</p>}
                      {school.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{school.email}</p>}
                    </div>
                  )}

                  {/* Admin last login */}
                  <div
                    className={`text-xs flex items-center gap-1.5 ${getLastLoginColor(school.admin_last_login_at)}`}
                    title={school.admin_last_login_at ? new Date(school.admin_last_login_at).toLocaleString('es-PR') : 'Ningún administrador ha iniciado sesión'}
                    data-testid={`school-last-login-${school.code}`}
                  >
                    <Clock className="w-3 h-3" />
                    <span>
                      Admin: {formatLastLogin(school.admin_last_login_at)}
                      {school.admin_last_login_username && school.admin_count > 1 && (
                        <span className="text-slate-400 ml-1">({school.admin_last_login_username})</span>
                      )}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="pt-1 space-y-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(school)}
                        className="flex-1 border-slate-300 text-slate-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                        data-testid={`edit-school-${school.code}`}>
                        <Edit className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleActive(school)}
                        className={`flex-1 border-slate-300 ${school.is_active ? 'text-red-500 hover:bg-red-50 hover:border-red-300' : 'text-emerald-500 hover:bg-emerald-50 hover:border-emerald-300'}`}
                        data-testid={`toggle-school-${school.code}`}>
                        <Power className="w-3 h-3 mr-1" /> {school.is_active ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      <label className="cursor-pointer" title="Subir logo de la escuela">
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => e.target.files[0] && handleLogoUpload(school.id, e.target.files[0])}
                          data-testid={`upload-logo-${school.code}`} />
                        <div className="h-8 flex items-center justify-center rounded-md border border-slate-300 text-slate-500 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-300 transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                        </div>
                      </label>
                      <Button size="sm" variant="outline" onClick={() => sendSchoolAdminCredentials(school)}
                        title="Enviar credenciales al administrador (solo si no las ha recibido)"
                        className="border-slate-300 text-purple-500 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300"
                        data-testid={`send-credentials-school-${school.code}`}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resendSchoolAdminCredentials(school)}
                        title="Regenerar contraseña y reenviar (aunque ya haya recibido credenciales)"
                        className="border-slate-300 text-amber-500 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300"
                        data-testid={`resend-credentials-school-${school.code}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => impersonateSchoolAdmin(school)}
                        title="Entrar como admin de esta escuela (soporte técnico)"
                        className="border-slate-300 text-blue-500 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                        data-testid={`impersonate-school-${school.code}`}>
                        <LogIn className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteSchool(school)}
                        title="Eliminar escuela"
                        className="border-slate-300 text-red-400 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                        data-testid={`delete-school-${school.code}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </>)}

      {activeTab === 'settings' && (
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="smtp-settings-card">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2"><Mail className="w-5 h-5 text-blue-500" /> Configuración SMTP - Super Administrador</CardTitle>
            <p className="text-xs text-slate-500">Configure su servidor SMTP (Titan) para enviar credenciales automáticamente al crear nuevas escuelas.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Servidor SMTP</Label>
                <Input value={smtpConfig.smtp_host} onChange={(e) => setSmtpConfig({...smtpConfig, smtp_host: e.target.value})}
                  placeholder="smtp.titan.email" className="bg-white border-slate-200 text-slate-800" data-testid="sa-smtp-host" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Puerto</Label>
                <Input type="number" value={smtpConfig.smtp_port} onChange={(e) => setSmtpConfig({...smtpConfig, smtp_port: parseInt(e.target.value) || 587})}
                  className="bg-white border-slate-200 text-slate-800" data-testid="sa-smtp-port" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Email Remitente</Label>
                <Input value={smtpConfig.smtp_email} onChange={(e) => setSmtpConfig({...smtpConfig, smtp_email: e.target.value})}
                  placeholder="admin@jkenterprise.com" className="bg-white border-slate-200 text-slate-800" data-testid="sa-smtp-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Contraseña SMTP</Label>
                <Input type="password" value={smtpConfig.smtp_password} onChange={(e) => setSmtpConfig({...smtpConfig, smtp_password: e.target.value})}
                  placeholder="Dejar vacío para no cambiar" className="bg-white border-slate-200 text-slate-800" data-testid="sa-smtp-password" />
              </div>
            </div>
            <Button onClick={saveSmtpConfig} disabled={smtpLoading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white" data-testid="sa-smtp-save">
              {smtpLoading ? 'Guardando...' : 'Guardar Configuración SMTP'}
            </Button>

            {/* Deliverability guidance — avoid emails landing in spam */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="smtp-deliverability-guide">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Evitar que los correos caigan en Spam
              </p>
              <p className="text-xs text-amber-700">
                El sistema ya envía cada correo con versión en texto plano + HTML y encabezados correctos (Message-ID, Fecha, Responder-a).
                Lo que falta lo debe configurar cada escuela en su proveedor de dominio/DNS (SPF, DKIM y DMARC):
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-1">
                <div className="bg-white rounded-md p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-slate-700 mb-1">Gmail / Google Workspace</p>
                  <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                    <li>SPF (TXT en el dominio): <code className="bg-slate-100 px-1 rounded">v=spf1 include:_spf.google.com ~all</code></li>
                    <li>DKIM: Admin Console → Apps → Google Workspace → Gmail → Autenticar correo electrónico → generar y publicar la clave TXT, luego "Iniciar autenticación"</li>
                    <li>DMARC (TXT en <code className="bg-slate-100 px-1 rounded">_dmarc.tudominio.com</code>): <code className="bg-slate-100 px-1 rounded">v=DMARC1; p=quarantine; pct=100; rua=mailto:tu@correo.com</code></li>
                    <li>Con una cuenta @gmail.com personal (sin dominio propio) no se puede publicar SPF/DKIM propio — se recomienda Google Workspace con dominio propio.</li>
                  </ul>
                </div>
                <div className="bg-white rounded-md p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-slate-700 mb-1">Titan Email / dominio propio</p>
                  <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                    <li>SPF (TXT en el dominio): <code className="bg-slate-100 px-1 rounded">v=spf1 include:spf.titan.email ~all</code></li>
                    <li>DKIM: en el panel del proveedor de hosting/dominio (ej. Hostinger, Namecheap) → sección Email → activar DKIM y copiar el TXT generado</li>
                    <li>DMARC: mismo registro TXT que Google, en <code className="bg-slate-100 px-1 rounded">_dmarc.tudominio.com</code></li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-amber-700 pt-1">Estos registros se agregan una sola vez por dominio (no por escuela) y suelen tardar unas horas en propagarse.</p>
            </div>

            {/* IMAP — Archivar correos enviados */}
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <div>
                <Label className="text-slate-800 text-sm font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-emerald-600" />
                  IMAP — Guardar copia en "Enviados"
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Después de cada envío SMTP, la app guarda automáticamente una copia del correo en la carpeta <strong>Enviados</strong> de tu cuenta. Así tienes evidencia desde tu cliente de email (Gmail, Titan, Apple Mail, etc.). Reutiliza el email y contraseña SMTP de arriba — solo necesitas el servidor IMAP.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-600 text-xs">Servidor IMAP</Label>
                  <Input value={smtpConfig.imap_host}
                    onChange={(e) => setSmtpConfig({...smtpConfig, imap_host: e.target.value})}
                    placeholder="imap.titan.email" className="bg-white border-slate-200 text-slate-800"
                    data-testid="sa-imap-host" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-600 text-xs">Puerto IMAP</Label>
                  <Input type="number" value={smtpConfig.imap_port}
                    onChange={(e) => setSmtpConfig({...smtpConfig, imap_port: parseInt(e.target.value) || 993})}
                    className="bg-white border-slate-200 text-slate-800" data-testid="sa-imap-port" />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="sa-imap-ssl"
                    checked={smtpConfig.imap_ssl}
                    onChange={(e) => setSmtpConfig({...smtpConfig, imap_ssl: e.target.checked})}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="sa-imap-ssl" />
                  <Label htmlFor="sa-imap-ssl" className="text-slate-600 text-xs cursor-pointer">
                    Usar SSL/TLS (puerto 993). Desactivar solo para STARTTLS en puerto 143.
                  </Label>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                Dejar el servidor IMAP vacío desactiva el archivado. La app intentará detectar automáticamente la carpeta "Sent / Enviados / [Gmail]/Sent Mail" — si tu proveedor usa otro nombre y no funciona, contáctame.
              </p>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <Label className="text-slate-800 text-sm font-semibold">Probar configuración</Label>
              <p className="text-xs text-slate-500 mb-2">Envía un correo de prueba para verificar que la conexión funciona.</p>
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-slate-600 text-xs">Enviar prueba a:</Label>
                  <Input type="email" value={smtpTestEmail}
                    onChange={(e) => setSmtpTestEmail(e.target.value)}
                    placeholder={smtpConfig.smtp_email || 'tucorreo@ejemplo.com'}
                    className="bg-white border-slate-200 text-slate-800" data-testid="sa-smtp-test-email" />
                </div>
                <Button onClick={testSmtpConfig} disabled={smtpTesting}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white whitespace-nowrap"
                  data-testid="sa-smtp-test-btn">
                  {smtpTesting ? 'Enviando...' : 'Probar conexión'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                💡 Si puerto 587 falla, prueba con 465 (SSL). Hostname común Titan: <code>smtp.titan.email</code>
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      </div>

      {/* Create School Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <School className="w-5 h-5 text-orange-500" /> Nueva Escuela
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-700 text-xs">
                Se creará un usuario administrador para esta escuela automáticamente.
              </AlertDescription>
            </Alert>

            <div className="space-y-1">
              <Label className="text-slate-600 text-xs">Nombre de la escuela *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ej: Colegio San Juan Bautista" className="bg-white border-slate-200 text-slate-800" data-testid="create-school-name" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Dirección</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Teléfono</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" />
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs flex items-center gap-1"><Palette className="w-3 h-3" /> Colores de la escuela</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'primary_color', label: 'Primario' },
                  { key: 'secondary_color', label: 'Secundario' },
                  { key: 'accent_color', label: 'Acento' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <p className="text-[10px] text-slate-500">{label}</p>
                    <div className="flex items-center gap-2">
                      <input type="color" value={formData[key]}
                        onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                        className="w-8 h-8 rounded cursor-pointer border-0" />
                      <Input value={formData[key]} onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                        className="bg-white border-slate-200 text-slate-800 text-xs h-8 flex-1" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Admin Section */}
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <Label className="text-slate-600 text-xs">Administrador de la Escuela</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">Nombre completo *</Label>
                  <Input value={formData.admin_full_name} onChange={(e) => setFormData({...formData, admin_full_name: e.target.value})}
                    className="bg-white border-slate-200 text-slate-800" data-testid="create-admin-fullname" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">Email *</Label>
                  <Input value={formData.admin_email} onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                    type="email" className="bg-white border-slate-200 text-slate-800" data-testid="create-admin-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">Usuario *</Label>
                  <Input value={formData.admin_username} onChange={(e) => setFormData({...formData, admin_username: e.target.value})}
                    className="bg-white border-slate-200 text-slate-800" data-testid="create-admin-username" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">Contraseña *</Label>
                  <Input value={formData.admin_password} onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                    type="password" className="bg-white border-slate-200 text-slate-800" data-testid="create-admin-password" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}
              className="border-slate-300 text-slate-600">Cancelar</Button>
            <Button onClick={handleCreate}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
              data-testid="confirm-create-school">Crear Escuela</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit School Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" /> Editar Escuela
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-slate-600 text-xs">Nombre</Label>
              <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="bg-white border-slate-200 text-slate-800" data-testid="edit-school-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Dirección</Label>
                <Input value={editData.address} onChange={(e) => setEditData({...editData, address: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Teléfono</Label>
                <Input value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Email</Label>
                <Input value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs flex items-center gap-1"><Palette className="w-3 h-3" /> Colores</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'primary_color', label: 'Primario' },
                  { key: 'secondary_color', label: 'Secundario' },
                  { key: 'accent_color', label: 'Acento' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <p className="text-[10px] text-slate-500">{label}</p>
                    <div className="flex items-center gap-2">
                      <input type="color" value={editData[key]}
                        onChange={(e) => setEditData({...editData, [key]: e.target.value})}
                        className="w-8 h-8 rounded cursor-pointer border-0" />
                      <Input value={editData[key]} onChange={(e) => setEditData({...editData, [key]: e.target.value})}
                        className="bg-white border-slate-200 text-slate-800 text-xs h-8 flex-1" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}
              className="border-slate-300 text-slate-600">Cancelar</Button>
            <Button onClick={handleEdit}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white"
              data-testid="confirm-edit-school">Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
